/**
 * Firestore persistence for insurance policies/orders.
 *
 * Collection: `insurancePolicies`
 * A policy doc is created when an order is placed and updated as its status
 * changes (pending → approved → cancelled). Scoped by companyName (business)
 * and/or userId (individual member) so both dashboards can read their own.
 */
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { fireStoreDB } from "./firebaseConfig";

const COLLECTION = "insurancePolicies";

/** Create a policy record for a newly placed order. */
export async function savePolicy(policy) {
  const ref = await addDoc(collection(fireStoreDB, COLLECTION), {
    ...policy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Patch an existing policy record (e.g. status change after payment). */
export async function updatePolicy(docId, patch) {
  await updateDoc(doc(fireStoreDB, COLLECTION, docId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/** All policies for a business (by company name). */
export async function getPoliciesByCompany(companyName) {
  if (!companyName) return [];
  const q = query(
    collection(fireStoreDB, COLLECTION),
    where("companyName", "==", companyName),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** All policies belonging to an individual member (by uid). */
export async function getPoliciesByUser(userId) {
  if (!userId) return [];
  const q = query(
    collection(fireStoreDB, COLLECTION),
    where("userId", "==", userId),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
